/*
 * Copyright Mimic Networks, Inc. 2025.
 */

import { Modal } from 'antd';
import { useNavigate } from 'react-router-dom';

import { ApiError, Subtenant } from '@/client';
import { useMessage } from '@/hooks/message';
import { useDeleteSubtenantMutation } from '@/hooks/mutation/useDeleteSubtenantMutation';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { Container } from '@/primitives/Container';
import { Text } from '@/primitives/Text';
import { Title } from '@/primitives/Title';

type DeleteSubtenantModalProps = {
  deleteSubtenant: Subtenant | undefined;
  deleteIsPending: boolean;
  setDeleteIsPending: (isPending: boolean) => void;
  onCancelSubtenantDelete: () => void;
};

export function DeleteSubtenantModal({
  deleteSubtenant,
  deleteIsPending,
  setDeleteIsPending,
  onCancelSubtenantDelete,
}: DeleteSubtenantModalProps) {
  const { t } = useMimicTranslation('subtenants');

  const [message] = useMessage();
  const navigate = useNavigate();

  const { mutate: deleteSubtenantMutation } = useDeleteSubtenantMutation({
    onError: (error, subtenant) => {
      if (error instanceof ApiError) {
        if (error.status === 404) {
          message.error(t('confirmDelete.subtenantNotFound', subtenant));
          closeDeleteDialog();
          return;
        }
        if (error.status === 409) {
          message.error(t('confirmDelete.subtenantDeleteConflict', subtenant));
          closeDeleteDialog();
          return;
        }
      }
      message.error(t('confirmDelete.subtenantDeleteError', subtenant));
      setDeleteIsPending(false);
    },
    onSuccess: (_, deletedSubtenant) => {
      message.success(t('subtenantDeleted', deletedSubtenant));
      closeDeleteDialog();
      setDeleteIsPending(false);
      navigate(`/tenants/${deletedSubtenant.tenantID}/subtenants`);
    },
    onMutate: () => {
      setDeleteIsPending(true);
    },
  });

  const onSubtenantDelete = () => {
    deleteSubtenantMutation(deleteSubtenant!);
  };

  const closeDeleteDialog = () => {
    onCancelSubtenantDelete();
  };

  return (
    <Modal
      open={Boolean(deleteSubtenant)}
      onCancel={onCancelSubtenantDelete}
      onOk={onSubtenantDelete}
      cancelText={t('confirmDelete.cancel')}
      cancelButtonProps={{ disabled: deleteIsPending }}
      okText={t('confirmDelete.ok')}
      okButtonProps={{
        danger: true,
        loading: deleteIsPending,
      }}
    >
      <Container padding="sm">
        <Title level={4}>{t('confirmDelete.title')}</Title>
        <Text>{t('confirmDelete.body1', { name: deleteSubtenant?.name })}</Text>
      </Container>
    </Modal>
  );
}
